import Decimal from 'decimal.js';
import { TAX_PIPELINE } from './taxPipelineConfig';
import type { DisposalDetail, FifoResult } from './types';

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

/** Whole-year holding convention for deemed acquisition cost % (Finnish deemed-cost method). */
export function holdingYearsBetween(acquiredMs: number, disposedMs: number): number {
  return (disposedMs - acquiredMs) / MS_PER_YEAR;
}

export type DeemedHoldingBracket = '<10y' | '>=10y' | 'unknown';

/**
 * Deemed acquisition cost as a fraction of proceeds (VNA-style simplification used with `basis_method` deemed/best).
 * Unknown acquisition time → treat as &lt;10y (20%) for conservatism.
 */
export function deemedCostBasisFromProceeds(
  proceedsEur: Decimal,
  holdingYears: number | null
): { deemedCostBasisEur: Decimal; deemedHoldingBracket: DeemedHoldingBracket } {
  const bracket: DeemedHoldingBracket =
    holdingYears == null ? 'unknown' : holdingYears < 10 ? '<10y' : '>=10y';
  const fraction = holdingYears == null || holdingYears < 10 ? new Decimal('0.2') : new Decimal('0.4');
  return {
    deemedCostBasisEur: proceedsEur.mul(fraction),
    deemedHoldingBracket: bracket,
  };
}

/**
 * Enrich FIFO disposal lines with Finnish deemed / chosen basis (auditable alongside FIFO).
 * `costEur` / `gainEur` on each detail remain **FIFO engine** values; chosen fields reflect `TAX_PIPELINE.fi.basisMethod`
 * (`best` = min(fifo_gain, deemed_gain); deemed uses 20%/40% of proceeds by holding bracket).
 */
const PROCEEDS_DEEMED_EPS = new Decimal('1e-12');

function proceedsTooSmallForDeemed(proceeds: Decimal): boolean {
  return proceeds.lte(0) || proceeds.abs().lte(PROCEEDS_DEEMED_EPS);
}

export function applyFiBasisToDisposalDetails(details: DisposalDetail[]): DisposalDetail[] {
  const mode = TAX_PIPELINE.fi.basisMethod;
  return details.map((d) => {
    const proceeds = d.proceedsEur;
    const fifoCost = d.costEur;
    const fifoGain = d.gainEur;
    const acquired = d.lotAcquiredAt;
    const holdingYears =
      acquired != null ? holdingYearsBetween(acquired, d.ts) : null;
    const { deemedCostBasisEur, deemedHoldingBracket } = deemedCostBasisFromProceeds(
      proceeds,
      holdingYears
    );
    const deemedGain = proceeds.minus(deemedCostBasisEur);

    let basisMethodUsed: 'fifo' | 'deemed';
    let chosenCostBasisEur: Decimal;
    let chosenGainEur: Decimal;
    let deemedPreventedDueToZeroProceeds = false;

    if (proceedsTooSmallForDeemed(proceeds)) {
      basisMethodUsed = 'fifo';
      chosenCostBasisEur = fifoCost;
      chosenGainEur = fifoGain;
      if (mode === 'deemed' || mode === 'best') {
        deemedPreventedDueToZeroProceeds = true;
      }
    } else if (mode === 'fifo') {
      basisMethodUsed = 'fifo';
      chosenCostBasisEur = fifoCost;
      chosenGainEur = fifoGain;
    } else if (mode === 'deemed') {
      basisMethodUsed = 'deemed';
      chosenCostBasisEur = deemedCostBasisEur;
      chosenGainEur = deemedGain;
    } else {
      if (fifoGain.lte(deemedGain)) {
        basisMethodUsed = 'fifo';
        chosenCostBasisEur = fifoCost;
        chosenGainEur = fifoGain;
      } else {
        basisMethodUsed = 'deemed';
        chosenCostBasisEur = deemedCostBasisEur;
        chosenGainEur = deemedGain;
      }
    }

    return {
      ...d,
      fifoCostBasisEur: fifoCost,
      deemedCostBasisEur,
      chosenCostBasisEur,
      basisMethodUsed,
      fifoGainEur: fifoGain,
      deemedGainEur: deemedGain,
      chosenGainEur,
      holdingYearsAtDisposal: holdingYears,
      deemedHoldingBracket,
      deemedPreventedDueToZeroProceeds,
    };
  });
}

/** Recompute gain/loss totals using chosen gains when present (filing-aligned when `basis_method` is not fifo-only). */
/**
 * Human-readable filing rule for CSV exports when `basis_method` is fifo / deemed / best.
 * Repeats on each row so standalone files stay auditable without the app.
 */
export function fiBasisFilingAuditNote(): string {
  const mode = TAX_PIPELINE.fi.basisMethod;
  if (mode === 'fifo') {
    return 'Filing uses FIFO only: chosen columns match FIFO cost and gain.';
  }
  if (mode === 'deemed') {
    return 'Filing uses deemed acquisition cost: 20% of proceeds if holding <10y or unknown, 40% if >=10y (see deemed_holding_bracket).';
  }
  return (
    'basis_method=best: chosen_gain = min(fifo_gain, deemed_gain); ' +
    'basis_method_used = fifo when fifo_gain <= deemed_gain, else deemed; ' +
    'deemed cost = 20% of proceeds if <10y or unknown, 40% if >=10y.'
  );
}

/** Rows where deemed was not applied because proceeds were effectively zero. */
export function countDeemedPreventedDueToZeroProceeds(details: DisposalDetail[]): number {
  return details.reduce(
    (n, d) => n + (d.deemedPreventedDueToZeroProceeds === true ? 1 : 0),
    0
  );
}

export function applyFiBasisToFifoResult(result: FifoResult): FifoResult {
  const details = applyFiBasisToDisposalDetails(result.details);
  let totalGainEur = new Decimal(0);
  let totalLossEur = new Decimal(0);
  for (const d of details) {
    const g = d.chosenGainEur ?? d.gainEur;
    if (g.gte(0)) totalGainEur = totalGainEur.plus(g);
    else totalLossEur = totalLossEur.plus(g.abs());
  }
  return {
    ...result,
    details,
    totalGainEur,
    totalLossEur,
  };
}
